// src/pages/Users.tsx
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Users.css';
import { 
  FiSearch, 
  FiEye, 
  FiLock, 
  FiUnlock, 
  FiUserCheck, 
  FiUsers, 
  FiUserPlus, 
  FiUserMinus,
  FiFileText,
  FiCalendar,
  FiMapPin,
  FiBriefcase,
  FiCreditCard,
  FiShield,
  FiTrendingUp,
  FiActivity,
  FiAlertCircle,
  FiMail,
  FiPhone,
  FiImage,
  FiCamera,
  FiGlobe,
  FiClock,
  FiNavigation
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';

// ============ LOCATION INTERFACE ============
interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  timestamp: string;
}

// ============ UPDATED USER INTERFACE ============
interface User {
  user_id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dssn: string | null;
  is_verified: number;
  is_active: boolean;
  created_at: string;
  address: string | null;
  postal_address: string | null;
  image: string | null;
  image_url: string | null;
  DATE_OF_BIRTH: string | null;
  PLACE_OF_BIRTH: string | null;
  NATIONALITY: string | null;
  MARITAL_STATUS: string | null;
  EMPLOYMENT_STATUS: string | null;
  NUMBER_OF_CHILDREN: number | null;
  SEX: string | null;
  PASSPORT_NUMBER: string | null;
  BIRTH_CERTIFICATE: string | null;
  DRIVER_LICENSE: string | null;
  passport_image: string | null;
  passport_image_url: string | null;
  birth_certificate_image: string | null;
  birth_certificate_image_url: string | null;
  drivers_license_image: string | null;
  drivers_license_image_url: string | null;
  national_id_image: string | null;
  national_id_image_url: string | null;
  wallet?: {
    usd_balance: number;
    lrd_balance: number;
    status: string;
  };
  live_location: LocationData | null;
  location_history: LocationData[];
}

interface DashboardStats {
  total_users: number;
  users_with_dssn: number;
  users_without_dssn: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

// ============ HELPER: Parse location data safely ============
const parseLocation = (loc: any): LocationData | null => {
  if (!loc) return null;
  
  return {
    latitude: typeof loc.latitude === 'string' ? parseFloat(loc.latitude) : loc.latitude,
    longitude: typeof loc.longitude === 'string' ? parseFloat(loc.longitude) : loc.longitude,
    accuracy: loc.accuracy ? (typeof loc.accuracy === 'string' ? parseFloat(loc.accuracy) : loc.accuracy) : null,
    altitude: loc.altitude ? (typeof loc.altitude === 'string' ? parseFloat(loc.altitude) : loc.altitude) : null,
    speed: loc.speed ? (typeof loc.speed === 'string' ? parseFloat(loc.speed) : loc.speed) : null,
    heading: loc.heading ? (typeof loc.heading === 'string' ? parseFloat(loc.heading) : loc.heading) : null,
    timestamp: loc.timestamp || new Date().toISOString(),
  };
};

// ============ SKELETON COMPONENTS ============
const SkeletonCard: React.FC = () => (
  <div className="stat-card glass skeleton-card">
    <div className="skeleton-icon"></div>
    <div className="skeleton-info">
      <div className="skeleton-value"></div>
      <div className="skeleton-title"></div>
    </div>
  </div>
);

const SkeletonRow: React.FC = () => (
  <div className="skeleton-row">
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
    <div className="skeleton-cell"></div>
  </div>
);

// ============ IMAGE VIEWER COMPONENT ============
const ImageViewer: React.FC<{ src: string | null; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => {
  if (!src) return null;
  
  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-content" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} />
        <button className="image-viewer-close" onClick={onClose}>×</button>
      </div>
    </div>
  );
};

// ============ LOCATION MAP COMPONENT ============
const LocationMap: React.FC<{ location: LocationData }> = ({ location }) => {
  const [mapError, setMapError] = useState(false);

  if (!location) return null;

  const lat = location.latitude;
  const lng = location.longitude;
  
  const bbox = `${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}`;
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
  const fullMapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`;

  return (
    <div className="location-map-container">
      {!mapError ? (
        <iframe
          width="100%"
          height="250"
          style={{ border: 0, borderRadius: '8px' }}
          loading="lazy"
          allowFullScreen
          src={mapUrl}
          title="User Location Map"
          onError={() => setMapError(true)}
        />
      ) : (
        <div className="map-error-fallback">
          <FiMapPin size={40} />
          <p>Map unavailable</p>
          <a href={fullMapUrl} target="_blank" rel="noopener noreferrer">
            Open in OpenStreetMap →
          </a>
        </div>
      )}
      <div className="map-link">
        <a 
          href={fullMapUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          View on OpenStreetMap →
        </a>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const Users: React.FC = () => {
  const { token, employee } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewingImage, setViewingImage] = useState<{ src: string; alt: string } | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    users_with_dssn: 0,
    users_without_dssn: 0
  });
  const [successMessage, setSuccessMessage] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  const abortControllerRef = useRef<AbortController | null>(null);

  // ============ AUTO-REFRESH STATE ============
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clean up refresh interval
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  // ============ AUTO-REFRESH EFFECT ============
  useEffect(() => {
    // If a user is selected and auto-refresh is on
    if (selectedUser && isAutoRefresh) {
      // Clear existing interval
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
      
      // Start new interval - refresh every 10 seconds
      const interval = setInterval(() => {
        console.log('🔄 Auto-refreshing location for:', selectedUser.dssn);
        
        // Refresh the user data
        const refreshUser = async () => {
          try {
            const response = await axios.get(
              `${API_URL}/employees/users/search?dssn=${encodeURIComponent(selectedUser.dssn || '')}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
            
            if (response.data.data.user) {
              const user = response.data.data.user;
              const parsedUser = {
                ...user,
                live_location: parseLocation(user.live_location),
                location_history: user.location_history?.map((loc: any) => parseLocation(loc)).filter(Boolean) || []
              };
              
              // Update the selected user
              setSelectedUser(parsedUser);
              setLastRefreshTime(new Date());
              
              // Also update the user in the users list
              setUsers(prevUsers => 
                prevUsers.map(u => 
                  u.user_id === parsedUser.user_id ? parsedUser : u
                )
              );
            }
          } catch (error) {
            console.error('Auto-refresh failed:', error);
          }
        };
        
        refreshUser();
      }, 10000); // 10 seconds
      
      setRefreshInterval(interval);
      
      // Cleanup on unmount or when dependencies change
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Cleanup when no user is selected or auto-refresh is off
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [selectedUser?.dssn, isAutoRefresh]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees/users/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/employees/users`, {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Parse and fix location data for all users
      const parsedUsers = response.data.data.users.map((user: any) => ({
        ...user,
        live_location: parseLocation(user.live_location),
        location_history: user.location_history?.map((loc: any) => parseLocation(loc)).filter(Boolean) || []
      }));
      
      setUsers(parsedUsers);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch users:', error);
      }
    } finally {
      if (controller === abortControllerRef.current) {
        setLoading(false);
      }
    }
  };

  const searchByDSSN = async () => {
    if (!search.trim()) {
      fetchUsers();
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/employees/users/search?dssn=${encodeURIComponent(search)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.data.user) {
        const user = response.data.data.user;
        // Parse location data
        const parsedUser = {
          ...user,
          live_location: parseLocation(user.live_location),
          location_history: user.location_history?.map((loc: any) => parseLocation(loc)).filter(Boolean) || []
        };
        setUsers([parsedUser]);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const freezeUser = async (userUuid: string) => {
    if (!window.confirm('Are you sure you want to freeze this user?')) return;
    try {
      await axios.post(`${API_URL}/employees/users/${userUuid}/freeze`, 
        { reason: 'Manual freeze by employee' },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      fetchUsers();
      fetchStats();
      setSuccessMessage({
        show: true,
        message: 'User has been frozen successfully.'
      });
      setTimeout(() => setSuccessMessage({ show: false, message: '' }), 3000);
    } catch (error) {
      console.error('Failed to freeze user:', error);
      setSuccessMessage({
        show: true,
        message: 'Failed to freeze user. Please try again.'
      });
      setTimeout(() => setSuccessMessage({ show: false, message: '' }), 3000);
    }
  };

  const unfreezeUser = async (userUuid: string) => {
    if (!window.confirm('Are you sure you want to unfreeze this user?')) return;
    try {
      await axios.post(`${API_URL}/employees/users/${userUuid}/unfreeze`, {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      fetchUsers();
      fetchStats();
      setSuccessMessage({
        show: true,
        message: 'User has been unfrozen successfully.'
      });
      setTimeout(() => setSuccessMessage({ show: false, message: '' }), 3000);
    } catch (error) {
      console.error('Failed to unfreeze user:', error);
      setSuccessMessage({
        show: true,
        message: 'Failed to unfreeze user. Please try again.'
      });
      setTimeout(() => setSuccessMessage({ show: false, message: '' }), 3000);
    }
  };

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchByDSSN();
    }
  };

  const handleReset = () => {
    setSearch('');
    fetchUsers();
  };

  const canFreeze = employee?.role === 'super_admin' || employee?.role === 'customer_support' || employee?.role === 'compliance_officer';
  const canViewDetails = employee?.role === 'super_admin' || employee?.role === 'kyc_officer' || employee?.role === 'customer_support' || employee?.role === 'compliance_officer' || employee?.role === 'auditor';

  const statCards = [
    { 
      title: 'Total Users', 
      value: stats.total_users, 
      icon: <FiUsers />, 
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      subtitle: 'Registered accounts',
      trend: '+12%'
    },
    { 
      title: 'With DSSN', 
      value: stats.users_with_dssn, 
      icon: <FiUserCheck />, 
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      subtitle: 'Verified identity',
      trend: '+8%'
    },
    { 
      title: 'Without DSSN', 
      value: stats.users_without_dssn, 
      icon: <FiUserPlus />, 
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      subtitle: 'Pending verification',
      trend: '-3%'
    },
  ];

  const documentSections = [
    { 
      title: 'Passport', 
      imageUrl: selectedUser?.passport_image_url, 
      imageField: 'passport_image',
      documentNumber: selectedUser?.PASSPORT_NUMBER,
      icon: <FiFileText />
    },
    { 
      title: 'Birth Certificate', 
      imageUrl: selectedUser?.birth_certificate_image_url, 
      imageField: 'birth_certificate_image',
      documentNumber: selectedUser?.BIRTH_CERTIFICATE,
      icon: <FiFileText />
    },
    { 
      title: "Driver's License", 
      imageUrl: selectedUser?.drivers_license_image_url, 
      imageField: 'drivers_license_image',
      documentNumber: selectedUser?.DRIVER_LICENSE,
      icon: <FiFileText />
    },
    { 
      title: 'National ID', 
      imageUrl: selectedUser?.national_id_image_url, 
      imageField: 'national_id_image',
      documentNumber: null,
      icon: <FiFileText />
    },
  ];

  if (loading && users.length === 0) {
    return (
      <div className="users-page">
        <div className="ai-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="grid-overlay"></div>
        </div>
        <div className="users-container">
          <div className="page-header glass">
            <div className="skeleton-header">
              <div className="skeleton-title"></div>
              <div className="skeleton-subtitle"></div>
            </div>
          </div>
          <div className="stats-grid">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <div className="search-bar-container glass">
            <div className="skeleton-search"></div>
          </div>
          <div className="users-table-container glass">
            <div className="skeleton-table">
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      {viewingImage && (
        <ImageViewer 
          src={viewingImage.src} 
          alt={viewingImage.alt} 
          onClose={() => setViewingImage(null)} 
        />
      )}

      {successMessage.show && (
        <div className={`toast-notification ${successMessage.message.includes('Failed') ? 'error' : 'success'}`}>
          <FiAlertCircle />
          <span>{successMessage.message}</span>
        </div>
      )}

      <div className="ai-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="users-container">
        <div className="page-header glass">
          <div className="header-content">
            <div className="title-section">
              <div className="badge">
                <HiOutlineSparkles />
                <span>User Management</span>
              </div>
              <h1>User Registry</h1>
              <p>Manage and monitor all registered users across the platform</p>
            </div>
            <div className="header-stats">
              <div className="mini-stat">
                <FiTrendingUp />
                <span>Active Growth</span>
              </div>
              <div className="mini-stat">
                <FiActivity />
                <span>Live Monitoring</span>
              </div>
            </div>
          </div>
        </div>

        <div className="stats-grid">
          {statCards.map((stat, index) => (
            <div 
              key={index} 
              className="stat-card glass" 
              style={{ 
                background: stat.gradient + ', rgba(10, 10, 20, 0.4)'
              }}
            >
              <div className="stat-icon-wrapper">
                {stat.icon}
                <div className="stat-trend positive">{stat.trend}</div>
              </div>
              <div className="stat-info">
                <h3>{stat.value.toLocaleString()}</h3>
                <p>{stat.title}</p>
                <span className="stat-subtitle">{stat.subtitle}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="search-bar-container glass">
          <div className="search-bar">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by DSSN (e.g., PYGMNL94LR09Z24)..."
              value={search}
              onChange={(e) => setSearch(e.target.value.toUpperCase())}
              onKeyDown={handleSearch}
            />
            <span className="search-shortcut">↵</span>
          </div>
          <div className="search-actions">
            <button onClick={searchByDSSN} className="search-btn primary">
              <FiSearch /> Search
            </button>
            <button onClick={handleReset} className="search-btn secondary">
              <FiUserMinus /> Reset
            </button>
          </div>
        </div>

        <div className="users-table-container glass">
          <div className="table-header">
            <div className="table-title">
              <FiUsers />
              <span>Registered Users</span>
            </div>
            <div className="table-info">
              <span>{users.length} users found</span>
            </div>
          </div>
          
          {loading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <span>Loading users...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <FiUserMinus className="empty-icon" />
              <h3>No users found</h3>
              <p>
                {search 
                  ? `No users match the DSSN: ${search}`
                  : `No users found`
                }
              </p>
              {search && (
                <button onClick={handleReset} className="reset-btn">
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Contact</th>
                    <th>DSSN</th>
                    <th>Status</th>
                    <th>Wallet Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id} className="user-row">
                      <td>
                        <div className="user-cell">
                          {user.image_url ? (
                            <img 
                              src={user.image_url} 
                              alt={`${user.first_name} ${user.last_name}`}
                              className="user-avatar-img"
                              onClick={() => setViewingImage({ src: user.image_url!, alt: `${user.first_name} ${user.last_name}` })}
                            />
                          ) : (
                            <div className="user-avatar" style={{ background: `linear-gradient(135deg, #667eea, #764ba2)` }}>
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                          )}
                          <div className="user-info">
                            <div className="user-name">{user.first_name} {user.last_name}</div>
                            <div className="user-id">ID: {user.uuid?.slice(0, 8)}...</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-cell">
                          <span className="contact-email"><FiMail /> {user.email}</span>
                          <span className="contact-phone"><FiPhone /> {user.phone}</span>
                        </div>
                      </td>
                      <td className="dssn-cell">
                        <code>{user.dssn || '—'}</code>
                      </td>
                      <td>
                        <div className={`status-badge ${user.is_active ? 'active' : 'frozen'}`}>
                          <span className="status-dot"></span>
                          {user.is_active ? 'Active' : 'Frozen'}
                        </div>
                      </td>
                      <td>
                        <div className="wallet-cell">
                          <span className="usd-balance">${parseFloat(user.wallet?.usd_balance?.toString() || '0').toFixed(2)} USD</span>
                          <span className="lrd-balance">L${parseFloat(user.wallet?.lrd_balance?.toString() || '0').toFixed(2)} LRD</span>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {canViewDetails && (
                            <button className="action-icon view" onClick={() => setSelectedUser(user)} title="View Details">
                              <FiEye />
                            </button>
                          )}
                          {canFreeze && user.is_active && (
                            <button className="action-icon freeze" onClick={() => freezeUser(user.user_id.toString())} title="Freeze User">
                              <FiLock />
                            </button>
                          )}
                          {canFreeze && !user.is_active && (
                            <button className="action-icon unfreeze" onClick={() => unfreezeUser(user.user_id.toString())} title="Unfreeze User">
                              <FiUnlock />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-container glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <FiShield />
                <h2>User Profile Details</h2>
              </div>
              <div className="modal-actions">
                {/* Auto-refresh toggle button */}
                <button 
                  className={`refresh-toggle ${isAutoRefresh ? 'active' : ''}`}
                  onClick={() => {
                    setIsAutoRefresh(!isAutoRefresh);
                    if (!isAutoRefresh) {
                      setLastRefreshTime(new Date());
                    }
                  }}
                  title={isAutoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
                >
                  <FiActivity size={16} />
                  <span className="refresh-status">
                    {isAutoRefresh ? 'Live' : 'Paused'}
                  </span>
                  {isAutoRefresh && <span className="live-dot-small"></span>}
                </button>
                <button className="modal-close" onClick={() => {
                  setIsAutoRefresh(false);
                  setSelectedUser(null);
                }}>×</button>
              </div>
            </div>
            {lastRefreshTime && isAutoRefresh && (
              <div className="refresh-timestamp">
                Last updated: {lastRefreshTime.toLocaleTimeString()}
              </div>
            )}
            
            <div className="modal-body">
              <div className="profile-summary glass">
                {selectedUser.image_url ? (
                  <img 
                    src={selectedUser.image_url} 
                    alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                    className="profile-avatar-img"
                    onClick={() => setViewingImage({ src: selectedUser.image_url!, alt: `${selectedUser.first_name} ${selectedUser.last_name}` })}
                  />
                ) : (
                  <div className="profile-avatar" style={{ background: `linear-gradient(135deg, #667eea, #764ba2)` }}>
                    {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                  </div>
                )}
                <div className="profile-summary-info">
                  <h3>{selectedUser.first_name} {selectedUser.last_name}</h3>
                  <p><FiMail /> {selectedUser.email}</p>
                  <p><FiPhone /> {selectedUser.phone}</p>
                </div>
                <div className={`profile-status ${selectedUser.is_active ? 'active' : 'frozen'}`}>
                  {selectedUser.is_active ? 'Active Account' : 'Frozen Account'}
                </div>
              </div>

              <div className="profile-grid">
                <div className="profile-section glass">
                  <h3><FiUserCheck /> Personal Information</h3>
                  <div className="info-row">
                    <span className="label">Full Name:</span>
                    <span className="value">{selectedUser.first_name} {selectedUser.last_name}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">DSSN:</span>
                    <span className="value dssn">{selectedUser.dssn || 'Not Assigned'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Sex:</span>
                    <span className="value">{selectedUser.SEX || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Date of Birth:</span>
                    <span className="value">{selectedUser.DATE_OF_BIRTH ? new Date(selectedUser.DATE_OF_BIRTH).toLocaleDateString() : '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Place of Birth:</span>
                    <span className="value">{selectedUser.PLACE_OF_BIRTH || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Nationality:</span>
                    <span className="value">{selectedUser.NATIONALITY || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Marital Status:</span>
                    <span className="value">{selectedUser.MARITAL_STATUS || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Employment Status:</span>
                    <span className="value">{selectedUser.EMPLOYMENT_STATUS || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Children:</span>
                    <span className="value">{selectedUser.NUMBER_OF_CHILDREN || '—'}</span>
                  </div>
                </div>

                <div className="profile-section glass">
                  <h3><FiMapPin /> Address Information</h3>
                  <div className="info-row">
                    <span className="label">Address:</span>
                    <span className="value">{selectedUser.address || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Postal Address:</span>
                    <span className="value">{selectedUser.postal_address || '—'}</span>
                  </div>
                </div>

                {/* Live Location Section */}
                {selectedUser.live_location ? (
                  <div className="profile-section glass location-section">
                    <h3>
                      <FiNavigation /> Live Location
                      <span className="live-indicator">
                        <span className="live-dot"></span>
                        {isAutoRefresh ? 'LIVE' : 'STATIC'}
                      </span>
                    </h3>
                    <div className="info-row">
                      <span className="label">Latitude:</span>
                      <span className="value location-coord">
                        {selectedUser.live_location.latitude?.toFixed(6) || '—'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Longitude:</span>
                      <span className="value location-coord">
                        {selectedUser.live_location.longitude?.toFixed(6) || '—'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Accuracy:</span>
                      <span className="value">
                        {selectedUser.live_location.accuracy != null 
                          ? `${selectedUser.live_location.accuracy.toFixed(2)} meters` 
                          : '—'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">
                        <FiClock size={12} /> Last Updated:
                      </span>
                      <span className="value">
                        {selectedUser.live_location.timestamp 
                          ? new Date(selectedUser.live_location.timestamp).toLocaleString() 
                          : '—'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">
                        <FiGlobe size={12} /> Source:
                      </span>
                      <span className="value">Mobile GPS</span>
                    </div>
                    
                    <LocationMap location={selectedUser.live_location} />
                    
                    {selectedUser.location_history && selectedUser.location_history.length > 0 && (
                      <div className="location-history">
                        <h4>
                          <FiClock size={14} /> Location History 
                          <span className="history-count">
                            {selectedUser.location_history.length} points
                          </span>
                        </h4>
                        <div className="history-list">
                          {selectedUser.location_history.map((loc, idx) => (
                            <div key={idx} className="history-item">
                              <span className="history-time">
                                {loc.timestamp ? new Date(loc.timestamp).toLocaleTimeString() : '—'}
                              </span>
                              <span className="history-coords">
                                {loc.latitude?.toFixed(4) || '—'}, {loc.longitude?.toFixed(4) || '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="profile-section glass location-section no-location">
                    <h3><FiMapPin /> Live Location</h3>
                    <div className="no-location-message">
                      <FiMapPin size={32} className="no-location-icon" />
                      <p>No location data available</p>
                      <span className="no-location-subtext">
                        User hasn't shared location or is offline
                      </span>
                    </div>
                  </div>
                )}

                <div className="profile-section glass">
                  <h3><FiCreditCard /> Wallet Information</h3>
                  <div className="info-row">
                    <span className="label">USD Balance:</span>
                    <span className="value usd">${parseFloat(selectedUser.wallet?.usd_balance?.toString() || '0').toFixed(2)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">LRD Balance:</span>
                    <span className="value lrd">L${parseFloat(selectedUser.wallet?.lrd_balance?.toString() || '0').toFixed(2)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Wallet Status:</span>
                    <span className="value">{selectedUser.wallet?.status || '—'}</span>
                  </div>
                </div>

                <div className="profile-section glass">
                  <h3><FiCalendar /> Account Information</h3>
                  <div className="info-row">
                    <span className="label">Joined:</span>
                    <span className="value">{new Date(selectedUser.created_at).toLocaleString()}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Verified:</span>
                    <span className="value">{selectedUser.is_verified === 1 ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {documentSections.map((doc, idx) => (
                  (doc.imageUrl || doc.documentNumber) && (
                    <div className="profile-section glass" key={idx}>
                      <h3><FiFileText /> {doc.title}</h3>
                      {doc.documentNumber && (
                        <div className="info-row">
                          <span className="label">Document Number:</span>
                          <span className="value">{doc.documentNumber}</span>
                        </div>
                      )}
                      {doc.imageUrl && (
                        <div className="info-row document-image-row">
                          <span className="label">Document Image:</span>
                          <div className="document-thumbnail" onClick={() => setViewingImage({ src: doc.imageUrl!, alt: doc.title })}>
                            <img src={doc.imageUrl} alt={doc.title} />
                            <div className="thumbnail-overlay">
                              <FiCamera />
                              <span>Click to view</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => {
                setIsAutoRefresh(false);
                setSelectedUser(null);
              }} className="close-modal-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
